import {
  KEILOG_CORPORATE_FACILITY_MASTER_PATH,
  KEILOG_INSTALLMENT_PAYMENT_PATH,
  KEILOG_FRANCHISE_INVOICE_PATH,
  KEILOG_INSURER_PAYMENT_PATH,
  KEILOG_PAYMENT_VARIANCE_PATH,
  KEILOG_LOCATION_PAYMENT_PATH,
  KEILOG_CORPORATE_INVOICE_DETAIL_PATH,
  KEILOG_CORPORATE_MASTER_PATH,
  KEILOG_DOCUMENT_SET_PATH,
  KEILOG_FB_ACCOUNTS_PATH,
  KEILOG_FB_OUTPUT_PATH,
  KEILOG_FIRM_BANKING_PATH,
  KEILOG_INSURER_INVOICE_PATH,
  KEILOG_MONTHLY_CONFIRM_PATH,
  KEILOG_OUTCOME_REGISTRATION_PATH,
  KEILOG_PATIENT_REGISTRATION_ROUTE_PATH,
  KEILOG_PROGRESS_REPORT_PC_PATH,
  KEILOG_RECEIPT_PRINT_PATH,
  KEILOG_TACK_SEAL_PATH,
  KEILOG_RECEIPT_STATUS_PATH,
  KEILOG_RECEIPT_DEADLINE_PATH,
  KEILOG_RECEIPT_LIST_PATH,
  KEILOG_MONTHLY_CLOSING_PATH,
  KEILOG_REZEPT_PROCESSING_PATH,
  KEILOG_TREATMENT_RECORD_PATH,
  KEILOG_TREATMENT_REPORT_PATH,
  KEILOG_RECEIPT_MANAGEMENT_PATH,
  KEILOG_QUESTIONNAIRE_PATH,
  KEILOG_RESULTS_MANAGEMENT_PATH,
  KEILOG_RESULTS_SUMMARY_PATH,
  KEILOG_RESULT_CHANGE_PATH,
  KEILOG_RESULT_VARIANCE_PATH,
  KEILOG_INSURANCE_REVIEW_PATH,
  KEILOG_SALES_ANALYSIS_PATH,
  KEILOG_ACTIVE_USER_TREND_PATH,
  KEILOG_REPORT_DASHBOARD_PATH,
  KEILOG_SALES_FORECAST_PATH,
  KEILOG_FRANCHISE_SALES_PATH,
  KEILOG_DAILY_SALES_PATH,
  KEILOG_SCHEDULE_TOTAL_PATH,
  KEILOG_SCHEDULE_WEEKLY_PATH,
  KEILOG_DAILY_REPORT_PATH,
  KEILOG_VISIT_RECORD_ENTRY_PATH,
} from '../constants/paths';

/**
 * One source for the backend screen name, frontend route and build readiness.
 *
 * The dashboard (`/`) is deliberately not here: it is outside the menu and the permission
 * matrix (D15), opened by the sidebar logo, so no menu entry has to grant it and the shell's
 * menu-path guard leaves it alone.
 */
export const KEILOG_SCREENS = {
  'patient-management': {
    screen: 'PatientManagementScreen',
    path: '/patient-management',
    built: true,
  },
  // SCR-084 施術報告書 — 対象利用者 and the report form on one screen. Filed
  // under 利用者管理 in the BA catalogue, which is why it sits beside the other
  // two 利用者 destinations here and in `app/Tenants/Keilog/Config/rbac.php`.
  'treatment-report': {
    screen: 'TreatmentReportScreen',
    path: KEILOG_TREATMENT_REPORT_PATH,
    built: true,
  },
  // SCR-084-003 施術経過報告書 PC — the 施術家's 📈 tab beside 施術報告書, the second
  // child of 報告書 in 施術師/shell/rah-shell.js. `id`, `screen` and `path` must match
  // app/Tenants/Keilog/Config/rbac.php character for character (FIX-PLAN T-1).
  'progress-report': {
    screen: 'ProgressReportPcScreen',
    path: KEILOG_PROGRESS_REPORT_PC_PATH,
    built: true,
  },
  // 申請の承認 (委託先 / 本部 approval-detail.html) — emitted for `admin` principals only.
  'patient-approvals': {
    screen: 'PatientApprovalScreen',
    path: '/patient-approvals',
    built: true,
  },
  'consent-management': {
    screen: 'ConsentManagementScreen',
    path: '/consent-management',
    built: true,
  },
  // SCR-053 保険証確認・一覧. `screen` and `path` must match
  // app/Tenants/Keilog/Config/rbac.php character for character: the backend emits the menu from
  // that file and the frontend resolves the emitted path through this one, so any drift shows
  // up as a menu entry that renders but cannot be entered.
  'insurance-review': {
    screen: 'InsuranceReviewScreen',
    path: KEILOG_INSURANCE_REVIEW_PATH,
    built: true,
  },
  // 問診票 (SCR-056) — KEiLOG registers it, RAH reads the shared copy.
  questionnaire: {
    screen: 'QuestionnaireScreen',
    path: KEILOG_QUESTIONNAIRE_PATH,
    built: true,
  },
  // SCR-060 施術継続理由・状態記入書. `screen` and `path` must match
  // app/Tenants/Keilog/Config/rbac.php character for character: the backend emits the menu from
  // that file and the frontend resolves the emitted path through this one, so any drift between
  // them shows up as a menu entry that renders but cannot be entered.
  'continuation-reason': {
    screen: 'ContinuationReasonScreen',
    path: '/long_term_frequents/init',
    built: true,
  },
  // SCR-061-002 スケジュール管理 › 週間表示. RA2N already owns this path (its week calendar), so the
  // screen arrives through `router/componentOverrides.tsx` rather than `router/routes.tsx`, as 利用者別
  // does. `screen` / `path` stay character-for-character what `app/Tenants/Keilog/Config/rbac.php` emits.
  schedule: { screen: 'PractitionerScheduleScreen', path: KEILOG_SCHEDULE_WEEKLY_PATH, built: true },
  // SCR-061-003 スケジュール管理 › トータル, right after 週間表示 in the スタッフ shell. A tenant path
  // (`router/routes.tsx` + `router/ScheduleTotalRoute.tsx`).
  'schedule-total': { screen: 'ScheduleTotalScreen', path: KEILOG_SCHEDULE_TOTAL_PATH, built: true },
  // SCR-061-001 スケジュール管理 › 利用者別, which replaced SCR-064 利用者予定 at the same path
  // and menu id (Assume #1). RA2N already owns this path (訪問予定表(患者毎)), so the screen arrives
  // through `router/componentOverrides.tsx` rather than `router/routes.tsx`, and `screen` / `path`
  // stay character-for-character what `app/Tenants/Keilog/Config/rbac.php` emits.
  'patient-schedule': {
    screen: 'PatientScheduleScreen',
    path: '/visits/patient_init',
    built: true,
  },
  // SCR-061 基本スケジュール — last entry of 予定実績 › 予定 in all three concept shells (FIX-PLAN Q-C).
  // A tenant path (`router/routes.tsx` + `router/BaseScheduleRoute.tsx`); `screen` and `path` must
  // match app/Tenants/Keilog/Config/rbac.php character for character.
  'base-schedule': {
    screen: 'BaseScheduleScreen',
    path: '/base-schedule',
    built: true,
  },
  // SCR-073-001 実績管理 — first entry of 予定実績 › 実績 in all three concept shells. `screen` and
  // `path` must match app/Tenants/Keilog/Config/rbac.php character for character.
  results: {
    screen: 'ResultsManagementScreen',
    path: KEILOG_RESULTS_MANAGEMENT_PATH,
    built: true,
  },
  // SCR-071 施術録管理 — the six roles' one screen (Q-A), at a KEiLOG path of its own
  // (`router/routes.tsx`). `screen` and `path` must match
  // app/Tenants/Keilog/Config/rbac.php character for character.
  'treatment-records': {
    screen: 'TreatmentRecordScreen',
    path: KEILOG_TREATMENT_RECORD_PATH,
    built: true,
  },
  // SCR-074 実績変更管理 — after 施術録管理 under 予定実績 › 実績 (スタッフ/shell/rah-shell.js:101-106). `id`,
  // `screen` and `path` must match app/Tenants/Keilog/Config/rbac.php character for character.
  'result-change': {
    screen: 'ResultChangeScreen',
    path: KEILOG_RESULT_CHANGE_PATH,
    built: true,
  },
  // SCR-077 実績差異一覧 — 予定実績 › 実績 (本部管理 / FCオーナー / スタッフ) and レセプト処理 (委託先), filed
  // per role in `conceptMenu.ts`. `id`, `screen` and `path` must match
  // app/Tenants/Keilog/Config/rbac.php character for character.
  // SCR-403 月次確定 — 本部管理's 予定実績 › 実績, between 施術記録入力 and 実績差異一覧. `id`, `screen` and
  // `path` must match app/Tenants/Keilog/Config/rbac.php character for character.
  'monthly-confirm': {
    screen: 'MonthlyConfirmScreen',
    path: KEILOG_MONTHLY_CONFIRM_PATH,
    built: true,
  },
  'result-variance': {
    screen: 'ResultVarianceScreen',
    path: KEILOG_RESULT_VARIANCE_PATH,
    built: true,
  },
  // SCR-078 施術人数・売上合計 — under 予定実績 › 実績, after 実績差異一覧. `id`, `screen` and `path` must
  // match app/Tenants/Keilog/Config/rbac.php character for character.
  'results-summary': {
    screen: 'ResultsSummaryScreen',
    path: KEILOG_RESULTS_SUMMARY_PATH,
    built: true,
  },
  // SCR-086 施術報告書一覧. Filed under 訪問記録 next to 施術録管理, which is
  // where the 本部管理 concept puts it; the スタッフ concept files it under 帳票,
  // but KEiLOG has one menu tree rather than one per role.
  'treatment-report-list': {
    screen: 'TreatmentReportListScreen',
    path: '/treatment_reports/list',
    built: true,
  },
  // SCR-088 レセプト処理 — スタッフ / FCオーナー, at a KEiLOG path of its own (FIX-PLAN Q-A, Q-B). `screen`
  // and `path` must match app/Tenants/Keilog/Config/rbac.php (`rezept-list`) character for character.
  'rezept-list': { screen: 'RezeptProcessingScreen', path: KEILOG_REZEPT_PROCESSING_PATH, built: true },
  // SCR-129 法人請求書発行（詳細） — 入金精算 › 🧾 法人請求詳細 (BD 「Access」). `screen` and `path`
  // must match app/Tenants/Keilog/Config/rbac.php character for character.
  'corporate-invoice': {
    screen: 'CorporateInvoiceDetailScreen',
    path: KEILOG_CORPORATE_INVOICE_DETAIL_PATH,
    built: true,
  },
  // SCR-125 分割弁済管理 — 一般 › 入金精算 › 加盟店精算 in the 本部管理 concept. `screen` and `path` must
  // match app/Tenants/Keilog/Config/rbac.php character for character.
  'installment': {
    screen: 'InstallmentPaymentScreen',
    path: KEILOG_INSTALLMENT_PAYMENT_PATH,
    built: true,
  },
  // SCR-127 基幹店請求書 (本部管理, 入金精算 › 加盟店精算) / 基幹店請求詳細 (本部, 入金精算). `screen` and `path`
  // must match app/Tenants/Keilog/Config/rbac.php character for character.
  'franchise-invoice': {
    screen: 'FranchiseInvoiceScreen',
    path: KEILOG_FRANCHISE_INVOICE_PATH,
    built: true,
  },
  // SCR-113-001 保険者別入金 — 一般 › 入金精算 › 入金・消込 in the 本部管理 concept, and SCR-113-003 入金状況 —
  // 一般 › 精算入金 in the FCオーナー concept: one RBAC v2 row and path, the route draws the screen by active role.
  // `screen` and `path` must match app/Tenants/Keilog/Config/rbac_menu_v2.php character for character.
  'insurer-payment': {
    screen: 'InsurerPaymentScreen',
    path: KEILOG_INSURER_PAYMENT_PATH,
    built: true,
  },
  // SCR-115 入金差異 — 一般 › 入金精算 › 入金・消込, 本部管理 only (BD v2.0 A2). `screen` and `path` must
  // match app/Tenants/Keilog/Config/rbac.php character for character.
  'payment-variance': {
    screen: 'PaymentVarianceScreen',
    path: KEILOG_PAYMENT_VARIANCE_PATH,
    built: true,
  },
  // SCR-402 拠点別入金 — 一般 › 入金精算 › 入金・消込, 本部管理 and 本部 (FIX-PLAN Q-H). `screen` and `path` must
  // match app/Tenants/Keilog/Config/rbac.php character for character.
  'location-payment': {
    screen: 'LocationPaymentScreen',
    path: KEILOG_LOCATION_PAYMENT_PATH,
    built: true,
  },
  // SCR-029 法人マスタ管理 — マスタ › 組織 › 法人マスタ in the 本部管理 concept. 新規登録 (SCR-030) opens
  // as a drawer at `<path>/create`, and 編集 (SCR-206) will too; neither has a registry entry of its own.
  'corporate-master': {
    screen: 'CorporateMasterScreen',
    path: KEILOG_CORPORATE_MASTER_PATH,
    built: true,
  },
  // SCR-031 法人施設マスタ — the 🏗 法人施設 tab of 法人マスタ, no sidebar entry of its own: it follows the
  // `corporate-master` row (KEILOG_SCREEN_ROWS). 新規作成 and 編集 open as drawers on the list.
  'corporate-facility-master': {
    screen: 'CorporateFacilityMasterScreen',
    path: KEILOG_CORPORATE_FACILITY_MASTER_PATH,
    built: true,
  },
  // SCR-137-001 書類セット — under 本部管理's 帳票出力 (conceptMenu.ts).
  'document-set': { screen: 'DocumentSetScreen', path: KEILOG_DOCUMENT_SET_PATH, built: true },
  // SCR-133 総括票印刷 — under レセプト for 本部管理, 本部, 委託先, オーナー and スタッフ (総括票生成.html of each role).
  'insurer-invoice': { screen: 'InsurerInvoicePrintScreen', path: KEILOG_INSURER_INVOICE_PATH, built: true },
  // SCR-136 レセプト印刷 — under レセプト › 締め・印刷 for 本部管理 only (conceptMenu.ts).
  'receipt-print': { screen: 'ReceiptPrintScreen', path: KEILOG_RECEIPT_PRINT_PATH, built: true },
  // SCR-138 タックシール印刷 — 帳票出力 › 業務帳票 for 本部管理, 帳票 › 業務帳票 for FCオーナー and スタッフ
  // (BD 「Cách access」, BA 2026-10-26). `screen` and `path` must match
  // app/Tenants/Keilog/Config/rbac.php character for character.
  'tack-seal': { screen: 'TackSealPrintScreen', path: KEILOG_TACK_SEAL_PATH, built: true },
  // SCR-123 FB出力(全銀): 帳票出力 › データ出力 for 本部管理. `screen` and `path` must match
  // app/Tenants/Keilog/Config/rbac.php character for character.
  'fb-output': { screen: 'FbOutputScreen', path: KEILOG_FB_OUTPUT_PATH, built: true },
  // SCR-024 口座設定 (Account settings): 入金精算 › 口座設定, three menu entries on one path, one matrix row
  // each (`#user` / `#payee` / `#bank`, user 2026-10-14, reversing Q14). The id is tab 1's row; the three
  // rows are in KEILOG_SCREEN_ROWS. `screen` and `path` must match app/Tenants/Keilog/Config/rbac.php
  // character for character.
  'fc-debit-account': { screen: 'FbAccountsScreen', path: KEILOG_FB_ACCOUNTS_PATH, built: true },
  // SCR-224 to SCR-227 ファームバンキング (Firm banking): 入金精算 › ファームバンキング, four menu entries on one path
  // (`#data` / `#check` / `#make` / `#recv`), independently granted by the RBAC v2 screen rows. The id is tab
  // 1's menu entry. `screen` and `path` must match app/Tenants/Keilog/Config/rbac.php character for character.
  'fc-transfer-data': { screen: 'FirmBankingScreen', path: KEILOG_FIRM_BANKING_PATH, built: true },
  // SCR-093 全拠点 総括票・レセプト提出状況 — under レセプト for 本部管理 (› 処理) and 本部 (conceptMenu.ts).
  'receipt-status': { screen: 'ReceiptStatusScreen', path: KEILOG_RECEIPT_STATUS_PATH, built: true },
  // SCR-082 実績締め管理 — under レセプト › 締め・印刷 for 本部管理 and under レセプト for 本部 (rbac.php).
  // `id`, `screen` and `path` must match app/Tenants/Keilog/Config/rbac.php character for character.
  'receipt-deadline': { screen: 'ReceiptDeadlineScreen', path: KEILOG_RECEIPT_DEADLINE_PATH, built: true },
  // SCR-089 (BD SCR-402) レセプト状況 — under レセプト › 処理 for 本部管理, right after 全拠点状況. `id`, `screen`
  // and `path` must match app/Tenants/Keilog/Config/rbac.php character for character.
  'receipt-list': { screen: 'ReceiptListScreen', path: KEILOG_RECEIPT_LIST_PATH, built: true },
  // SCR-404 月間締め明細 — under 入金精算 (pay) for FCオーナー only (rbac.php `only_roles`). `id`, `screen` and
  // `path` must match app/Tenants/Keilog/Config/rbac.php character for character.
  'monthly-closing': { screen: 'MonthlyClosingScreen', path: KEILOG_MONTHLY_CLOSING_PATH, built: true },
  // SCR-SET1 利用者登録ルート — under 管理 › システム設定 for 本部管理者 only (rbac.php: alias of
  // RBAC権限管理 with `only_roles: ['hq_admin']`; the API checks the role itself).
  'patient-registration-route': {
    screen: 'PatientRegistrationRouteScreen',
    path: KEILOG_PATIENT_REGISTRATION_ROUTE_PATH,
    built: true,
  },
  'sales-trend': {
    screen: 'SalesTrendScreen',
    path: '/rezepts/statistical_init',
    built: false,
  },
  // SCR-148 売上分析 — the seven-tab report under 統計分析, for 本部管理 and 本部.
  // `screen` and `path` must match app/Tenants/Keilog/Config/rbac.php character for
  // character: the backend emits the menu from that file and the frontend resolves the
  // emitted path through this one.
  'sales-analysis': {
    screen: 'SalesAnalysisScreen',
    path: KEILOG_SALES_ANALYSIS_PATH,
    built: true,
  },
  // SCR-152 レポート総覧 — first in 統計分析 for 本部管理 and 本部.
  'report-dashboard': {
    screen: 'ReportDashboardScreen',
    path: KEILOG_REPORT_DASHBOARD_PATH,
    built: true,
  },
  // SCR-152 実働利用者数推移表 — under 統計分析 for 本部 only (`roles` in Config/rbac.php).
  'active-user-trend': {
    screen: 'ActiveUserTrendScreen',
    path: KEILOG_ACTIVE_USER_TREND_PATH,
    built: true,
  },
  // SCR-149 売上予測実績値表 (Sales forecast vs actual table): 統計分析 (Statistics analysis) › 売上予測 (Sales forecast), for 本部 (HQ) only. Same contract as
  // `sales-analysis` above: `screen` and `path` match Config/rbac.php character for character.
  'sales-forecast': {
    screen: 'SalesForecastScreen',
    path: KEILOG_SALES_FORECAST_PATH,
    built: true,
  },
  // SCR-150 基幹店売上 (Franchise sales): 統計分析 (Statistics analysis) › 基幹店売上 for 本部 (HQ). Same contract as
  // `sales-analysis` above: `screen` and `path` match Config/rbac.php character for character.
  'franchise-sales': {
    screen: 'FranchiseSalesScreen',
    path: KEILOG_FRANCHISE_SALES_PATH,
    built: true,
  },
  // SCR-151 売上日計表 (Daily sales): 統計分析 (Statistics) › 売上日計表, for 本部管理 (HQ admin) and 本部 (HQ). `screen`
  // and `path` match Config/rbac.php character for character.
  'daily-sales': {
    screen: 'DailySalesScreen',
    path: KEILOG_DAILY_SALES_PATH,
    built: true,
  },
  // 基幹店マスタ — `clinics` is KEiLOG's 基幹店, `clinic_locations` its 拠点
  // (confirmed by the customer in 要望機能一覧, 2026-02-25). Two screens, two ids.
  'clinic-master': {
    screen: 'ClinicMasterScreen',
    path: '/clinics/init',
    built: true,
  },
  'hq-settings': { screen: 'CompanyMasterScreen', path: '/hq-settings', built: true },
  'clinic-locations': {
    screen: 'ClinicLocationMasterScreen',
    path: '/clinic-location-master',
    built: true,
  },
  // SCR-020 保険診療料金: a tenant path, core's `medical_expense_prices/init` has no seam.
  'medical-expense-prices': {
    screen: 'MedicalExpensePriceScreen',
    path: '/insurance-fee-master',
    built: true,
  },
  // 自由診療料金 (SCR-018) — a tenant path: core draws `free_medical/init` as a static element, which a
  // tenant route never wins. `Config/rbac.php` spells the same path (design §4).
  'free-medical-prices': {
    screen: 'FreeMedicalPriceScreen',
    path: '/free-treatment-price',
    built: true,
  },
  // 税率マスタ (機能一覧 No.36) — the 🏷 tab of the 料金・書式 row. `Config/rbac.php` spells the same path.
  'tax-rates': {
    screen: 'TaxRateMasterScreen',
    path: '/tax-rate-master',
    built: true,
  },
  // SCR-160 お知らせ管理 — システム設定 › 設定 › お知らせ管理 for 本部管理 and 本部. `Config/rbac.php` spells the same path.
  information: {
    screen: 'InformationMasterScreen',
    path: '/information-master',
    built: true,
  },
  // SCR-162 通知ログ — システム設定 › 設定 › 通知ログ for 本部管理 and 本部. `Config/rbac.php` spells the same path.
  'notification-log': {
    screen: 'NotificationLogScreen',
    path: '/notification-log',
    built: true,
  },
  // SCR-162_01 受信通知 — 一般 › 🔔 通知 for オーナー and スタッフ. `Config/rbac.php` spells the same path.
  'received-notifications': {
    screen: 'ReceivedNotificationsScreen',
    path: '/notifications',
    built: true,
  },
  'insurance-switch': {
    screen: 'InsuranceSwitchScreen',
    path: '/insurance-switch',
    built: true,
  },
  'insurer-master': {
    screen: 'InsurerMasterScreen',
    path: '/insurers/init',
    built: true,
  },
  'document-formats': {
    screen: 'DocumentFormatMasterScreen',
    path: '/document_templates/init',
    built: true,
  },
  'therapist-management': {
    screen: 'PractitionerMasterScreen',
    path: '/massagers/init',
    built: true,
  },
  // 医療機関マスタ — the 🏥 tab of the 現場マスタ bar; a tenant path (one master per tenant, assum.md CL-002).
  'medical-institutions': {
    screen: 'MedicalInstitutionMasterScreen',
    path: '/hospital-master',
    built: true,
  },
  'office-data': { screen: 'OfficeDataScreen', path: '/office-data', built: true },
  // SCR-034 事業所リスト — 🏬 tab of the 現場マスタ bar (business-destination-master.html:1482).
  'business-destinations': { screen: 'BusinessDestinationMasterScreen', path: '/business-destinations', built: true },
  // ケアマネマスタ — 👩‍⚕ tab of the オーナー / スタッフ 現場マスタ bar (care-manager-master.html:365).
  'care-managers': { screen: 'CareManagerMasterScreen', path: '/care-managers', built: true },
  // 🏘 同一建物 tab of the same bar (same-building-master.html:1483).
  'same-buildings': { screen: 'SameBuildingScreen', path: '/same-building-master', built: true },
  'doctor-management': {
    screen: 'DoctorMasterScreen',
    path: '/doctor-master',
    built: true,
  },
  'permission-settings': {
    screen: 'RbacPermissionManagementScreen',
    path: '/settings/permissions',
    built: true,
  },
  'user-master': {
    screen: 'UserAccountMasterScreen',
    path: '/user-master',
    built: true,
  },
  // SCR-065 訪問スケジュール — a tenant path (`KEILOG_VISIT_SCHEDULE_PATH`). The
  // `schedule` entry above keeps RA2N's `/visits/schedule` for SCR-061.
  'visit-schedule': {
    screen: 'VisitScheduleScreen',
    path: '/visit-schedule',
    built: true,
  },
  'daily-report': { screen: 'DailyReportScreen', path: KEILOG_DAILY_REPORT_PATH, built: true },
  'visit-record': { screen: 'VisitRecordScreen', path: KEILOG_VISIT_RECORD_ENTRY_PATH, built: true },
  // SCR-221 転帰登録 — レセプト › after レセプト処理 for オーナー / スタッフ, at the head of 委託先's
  // レセプト処理 group, and under 本部管理's レセプト › 処理 with a 参照 badge; `conceptMenu.ts` lays
  // those four out. `id`, `screen` and `path` must match app/Tenants/Keilog/Config/rbac.php
  // character for character, or the entry renders and cannot be entered.
  'outcome-registration': {
    screen: 'OutcomeRegistrationScreen',
    path: KEILOG_OUTCOME_REGISTRATION_PATH,
    built: true,
  },
  // SCR-135 領収書・請求書 — 帳票 › 提出帳票 in the スタッフ concept (shell/rah-shell.js:183-186).
  'receipt-management': {
    screen: 'ReceiptManagementScreen',
    path: KEILOG_RECEIPT_MANAGEMENT_PATH,
    built: true,
  },
} as const;

export type KeilogScreenId = keyof typeof KEILOG_SCREENS;

export const KEILOG_SCREEN_PATHS = Object.fromEntries(
  Object.entries(KEILOG_SCREENS).map(([id, definition]) => [id, definition.path])
) as { [K in KeilogScreenId]: (typeof KEILOG_SCREENS)[K]['path'] };

/** Every route root of the bundle, widened to `string` so a caller may test any path against it. */
export const KEILOG_MENU_ROUTES: readonly { screen: string; path: string }[] = Object.values(KEILOG_SCREENS).map(
  ({ screen, path }) => ({ screen, path })
);

export const KEILOG_BUILT_SCREEN_PATHS: readonly string[] = Object.values(KEILOG_SCREENS)
  .filter(definition => definition.built)
  .map(definition => definition.path);

/**
 * The matrix row (`Config/rbac.php` `matrix.rows`) each screen belongs to.
 *
 * A screen with an entry of its own in the sidebar is opened by that entry. One without (a tab or
 * a detail of its row: 切替履歴, トータル, 実働者推移, 施術経過報告書 …) follows the grant of its
 * row, as an alias does: the shell lets it open when the menu shows an
 * entry of that row. 申請の承認 serves every 承認 kind, so it names each kind's row.
 */
export const KEILOG_SCREEN_ROWS: Readonly<Record<KeilogScreenId, readonly string[]>> = {
  'patient-management': ['patient-management', 'send-receive'],
  'treatment-report': ['treatment-report-list'],
  'progress-report': ['progress-report'],
  // 利用者移管 (approval-user-transfer) is served since SCR-220 `patient-transfer.md` (`#kind=transfer`).
  // SCR-220 承認 › 実績変更 (DEV 2026-10-26): the row's entry opens this screen on `#kind=result`.
  'patient-approvals': [
    'approval-user-registration',
    'approval-user-transfer',
    'approval-insurance-switch',
    'approval-consent-renewal',
    'approval-result-change',
  ],
  'consent-management': ['consent-management', 'progress-report'],
  'insurance-review': ['insurance-review'],
  questionnaire: ['questionnaire'],
  'continuation-reason': ['continuation-reason'],
  // SCR-221 転帰登録: its own row, which owns both leaves (閲覧・登録 and 変更履歴一覧).
  'outcome-registration': ['outcome-registration'],
  schedule: ['schedule'],
  'schedule-total': ['schedule'],
  'patient-schedule': ['patient-schedule'],
  'base-schedule': ['base-schedule'],
  results: ['results'],
  'results-summary': ['results-summary'],
  'treatment-records': ['treatment-records'],
  'result-change': ['result-change'],
  'monthly-confirm': ['monthly-confirm'],
  'result-variance': ['result-variance'],
  'treatment-report-list': ['treatment-report-list'],
  'rezept-list': ['rezept-processing'],
  'corporate-invoice': ['corporate-invoice-detail'],
  'installment': ['installment'],
  'franchise-invoice': ['franchise-invoice'],
  // Its own sidebar entry, under 入金精算 › 入金・消込 (`Config/rbac.php` row `insurer-payment`).
  'insurer-payment': ['insurer-payment'],
  'payment-variance': ['payment-variance'],
  'location-payment': ['location-payment'],
  'corporate-master': ['corporate-master'],
  'corporate-facility-master': ['corporate-master'],
  'document-set': ['document-set'],
  'insurer-invoice': ['insurer-invoice'],
  'receipt-print': ['receipt-print'],
  'tack-seal': ['tack-seal'],
  'receipt-status': ['receipt-status'],
  'receipt-deadline': ['receipt-deadline'],
  // The imported ReceiptList screen implements Scope v2 source 28, whose canonical key
  // is rezept-list. The older rezept-list screen ID above remains the processing screen.
  'receipt-list': ['rezept-list'],
  'monthly-closing': ['monthly-closing'],
  'fb-output': ['fb-output'],
  // SCR-024: the bare path opens on any of its three tabs' rows; the route guard then checks the tab's own.
  'fc-debit-account': ['fc-debit-account', 'fc-requester-account', 'fc-bank-master'],
  // SCR-224 to SCR-227: each tab has its own RBAC v2 row.
  'fc-transfer-data': ['fc-firm-banking'],
  'sales-trend': ['sales-analysis'],
  'sales-analysis': ['sales-analysis'],
  'report-dashboard': ['report-dashboard'],
  'active-user-trend': ['report-dashboard'],
  'sales-forecast': ['sales-forecast'],
  'franchise-sales': ['franchise-sales'],
  'daily-sales': ['daily-sales'],
  'clinic-master': ['clinic-master'],
  'hq-settings': ['hq-settings'],
  'clinic-locations': ['clinic-locations'],
  'medical-expense-prices': ['medical-expense-prices'],
  'free-medical-prices': ['free-medical-prices'],
  'tax-rates': ['tax-rates'],
  information: ['information'],
  'notification-log': ['notification-log'],
  // SCR-162_01: opened by its own entry only; it reads two rows' leaves and belongs to neither.
  'received-notifications': ['received-notifications'],
  'insurance-switch': ['insurance-review'],
  'insurer-master': ['insurer-master'],
  'document-formats': ['document-formats'],
  'therapist-management': ['therapist-management'],
  'medical-institutions': ['medical-institutions'],
  'office-data': ['office-data'],
  'business-destinations': ['business-destinations'],
  // ケアマネマスタ opens on its own sidebar entry only, never on the 事業所リスト row: that entry hides for a
  // tenant-wide grant (`max_scope`), so 本部管理者 / 本部 get neither the tab nor the screen (2026-10-07).
  'care-managers': ['care-manager'],
  'same-buildings': ['same-buildings'],
  'doctor-management': ['doctor-management'],
  'permission-settings': ['permission-settings'],
  'patient-registration-route': ['permission-settings'],
  'user-master': ['user-master'],
  'visit-schedule': ['schedule'],
  'daily-report': ['daily-report'],
  'visit-record': ['visit-record'],
  'receipt-management': ['receipt-management'],
};

/** The rows of the screen registered at exactly `path` (a route root), or none. */
export const keilogScreenRows = (path: string): readonly string[] => {
  const id = (Object.keys(KEILOG_SCREENS) as KeilogScreenId[]).find(
    candidate => KEILOG_SCREENS[candidate].path === path
  );
  return id ? KEILOG_SCREEN_ROWS[id] : [];
};
